/**
 * Probe live BaaS state for homologation wizard steps and return
 * `{ [stepId]: true }` for completions that can be auto-checked.
 */

import { fetchBaasAccounts } from "../../../../Services/baasAccountsListApi";
import { fetchBaasApiMetrics } from "../../../../Services/baasApiApi";
import { fetchBaasCardAccounts } from "../../../../Services/baasCardAccountsApi";
import { fetchBaasCardPrograms } from "../../../../Services/baasCardProgramsApi";
import {
  fetchBaasEntities,
} from "../../../../Services/baasEntidadesApi";
import { fetchBaasInstanceDetail } from "../../../../Services/baasInstancesApi";
import { listPixKeys } from "../../../../Services/baasPixApi";
import { fetchBaasStablesWalletsForAccounts } from "../../../../Services/baasStablesWalletApi";
import {
  isBaasInstanceOwnerAccount,
  isBaasInstanceOwnerEntity,
  isVirtualInstanceShellAccount,
  resolveBaasInstanceOwnerEntityId,
} from "../../../../Services/baasVirtualShells";

const isApprovedKyb = (entity) => {
  if (!entity) return false;
  if (String(entity.kybStatus || "").toLowerCase() === "approved") return true;
  const verification = String(
    entity.verificationStatus || entity.verification_status || "",
  ).toUpperCase();
  return verification === "APPROVED";
};

const settledValue = (result, fallback) =>
  result?.status === "fulfilled" ? result.value : fallback;

/**
 * HML checklist step `itemApiCall` ("Chamar API partner").
 * Completes only on partner-facing api-metrics (already exclude IB proxies
 * tagged `X-Baas-Client: console|carteira` via PartnerApiTelemetryFilter).
 *
 * Never use API-key `lastUsedAt`: the console `/v1/ib/baas/...` proxy
 * authenticates with the instance `dev_` / HML key, so browsing the console
 * (or this detector's own probes) would false-complete the step.
 */
export const isHomologPartnerApiCallDetected = (metrics) => {
  const totalRequests = Number(
    metrics?.total_requests ?? metrics?.totalRequests ?? 0,
  );
  return Number.isFinite(totalRequests) && totalRequests > 0;
};

/**
 * @param {object} instance HML instance to probe
 * @param {{ steps?: Array<{ id: string, detect?: string|null }>, signal?: AbortSignal }} [options]
 * @returns {Promise<Record<string, boolean>>}
 */
export const detectHomologCompletions = async (instance, options = {}) => {
  if (!instance) return {};

  const steps = Array.isArray(options.steps) ? options.steps : [];
  const detectById = new Map(
    steps
      .filter((step) => step?.id && step.detect)
      .map((step) => [step.id, step.detect]),
  );
  if (detectById.size === 0) return {};

  const wanted = new Set(detectById.values());
  const detected = {};
  const mark = (detectKey, ok) => {
    if (!ok) return;
    for (const [stepId, key] of detectById.entries()) {
      if (key === detectKey) detected[stepId] = true;
    }
  };

  let ownerId = resolveBaasInstanceOwnerEntityId(instance);
  let entities = null;
  let accounts = null;

  const needOwner =
    wanted.has("ownerEntity") || wanted.has("ownerApproved");
  const needClients =
    wanted.has("clientPf") || wanted.has("clientPj") || wanted.has("ownerEntity") || wanted.has("ownerApproved");
  const needAccounts =
    wanted.has("account") ||
    wanted.has("pixKey") ||
    wanted.has("stables");

  if (needOwner && !ownerId) {
    try {
      const detail = await fetchBaasInstanceDetail(instance);
      ownerId =
        resolveBaasInstanceOwnerEntityId(detail) ||
        resolveBaasInstanceOwnerEntityId(instance);
    } catch {
      // keep empty
    }
  }

  const isClientCreatedEntity = (entity) => {
    if (!entity) return false;
    if (isBaasInstanceOwnerEntity(entity, ownerId)) return false;
    return true;
  };

  const entityHasClientDocument = (entity) => {
    const docs = Array.isArray(entity?.documents) ? entity.documents : [];
    return docs.some((doc) => Boolean(doc?.hasFile || doc?.fileUrl || doc?.number));
  };

  const parallel = [];

  if (needClients || needAccounts) {
    parallel.push(
      fetchBaasEntities(instance)
        .then((list) => {
          entities = Array.isArray(list) ? list : [];
          const clients = entities.filter(isClientCreatedEntity);
          if (clients.length > 0) {
            mark("ownerEntity", true);
          }
          if (clients.some((entity) => entity?.type === "pf")) {
            mark("clientPf", true);
          }
          if (clients.some((entity) => entity?.type === "pj")) {
            mark("clientPj", true);
          }
          if (
            clients.some((entity) => isApprovedKyb(entity) || entityHasClientDocument(entity))
          ) {
            mark("ownerApproved", true);
          }
          return entities;
        })
        .catch(() => {
          entities = [];
          return entities;
        }),
    );
  }

  if (wanted.has("cards")) {
    parallel.push(
      Promise.allSettled([
        fetchBaasCardPrograms(instance, { signal: options.signal }),
        fetchBaasCardAccounts(instance, { signal: options.signal }),
      ]).then(([programsResult, cardsResult]) => {
        const programs = settledValue(programsResult, []);
        const cards = settledValue(cardsResult, []);
        if (
          (Array.isArray(programs) && programs.length > 0) ||
          (Array.isArray(cards) && cards.length > 0)
        ) {
          mark("cards", true);
        }
      }),
    );
  }

  if (wanted.has("apiCall")) {
    parallel.push(
      fetchBaasApiMetrics(instance, { period: "7d" })
        .then((metrics) => {
          if (isHomologPartnerApiCallDetected(metrics)) {
            mark("apiCall", true);
          }
        })
        .catch(() => {
          // metrics unavailable — leave apiCall undetected
        }),
    );
  }

  await Promise.all(parallel);

  if (needAccounts) {
    try {
      accounts = await fetchBaasAccounts(instance, {
        entities: entities || undefined,
        includeBalances: false,
      });
    } catch {
      accounts = [];
    }

    const clientAccounts = (Array.isArray(accounts) ? accounts : []).filter((account) => {
      if (!account) return false;
      if (account.isInstanceRoot) return false;
      if (isVirtualInstanceShellAccount(account)) return false;
      if (isBaasInstanceOwnerAccount(account, ownerId)) return false;
      return true;
    });
    accounts = clientAccounts;

    if (clientAccounts.length > 0) {
      mark("account", true);
    }

    if (wanted.has("pixKey") && clientAccounts.length > 0) {
      const sample = clientAccounts.slice(0, 4);
      const keyResults = await Promise.allSettled(
        sample.map((account) => listPixKeys(instance, account.id)),
      );
      if (
        keyResults.some((result) => {
          const keys = settledValue(result, []);
          return Array.isArray(keys) && keys.length > 0;
        })
      ) {
        mark("pixKey", true);
      }
    }

    if (
      wanted.has("stables") &&
      Array.isArray(accounts) &&
      accounts.length > 0
    ) {
      try {
        const wallets = await fetchBaasStablesWalletsForAccounts(
          instance,
          accounts.slice(0, 8),
        );
        if (Array.isArray(wallets) && wallets.length > 0) {
          mark("stables", true);
        }
      } catch {
        // ignore
      }
    }
  }

  return detected;
};
